import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import type { CmpStaffNoteReturn } from "../../../graphql/types";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";
import CalendarDateInput from "../../common/dashboard/CalendarDateInput";
import {
  getTimeZoneCalendarDateEndUtcMilli,
  getTimeZoneCalendarDateStartUtcMilli,
} from "../../common/dashboard/formatters";
import SearchableSelectField from "../../common/dashboard/SearchableSelectField";
import MemberCommunicationEmailPreview from "./MemberCommunicationEmailPreview";
import { htmlToText } from "../Emails/emailBackendMappers";
import type { MemberCommunicationEmail } from "./MemberActivitySections";
import { SectionShell } from "../../common/dashboard/SectionShell";
import MemberEmailCaptureNotice from "./MemberEmailCaptureNotice";
import { useCmpOrganizationNoun } from "../../../contexts/CmpOrganizationNounContext";
import { DATE_FORMAT_MESSAGE } from "../../../utils/fieldMessages";

export type MemberStaffNote = Pick<
  CmpStaffNoteReturn,
  "id" | "body" | "createdAtUtcMilli" | "createdByUserId" | "createdByDisplayName"
> & { email?: MemberCommunicationEmail };

type StaffNoteSort = {
  key: "date" | "staff" | "type";
  direction: "asc" | "desc";
};

export type MemberStaffNotesCardProps = {
  id?: string;
  chamberId?: string;
  notes: readonly MemberStaffNote[];
  draftComment: string;
  onDraftCommentChange: (comment: string) => void;
  onAdd: (body: string) => boolean | void | Promise<boolean | void>;
  authorDisplayName: string;
  authorUserId: string;
  chamberTimeZone: string | null | undefined;
  pendingNote?: { body: string; createdAtUtcMilli: number } | null;
  loading?: boolean;
  notesLoading?: boolean;
  error?: string | null;
  readOnly?: boolean;
  highlightDraftChanges?: boolean;
  communicationLog?: boolean;
  emails?: readonly MemberCommunicationEmail[];
};

// Placeholders the backend returns when it could not resolve the author.
const unresolvedStaffNames = new Set(["", "staff name not recorded", "chamber owner"]);

/**
 * Shows the signed-in staff name on the notes they authored when the saved
 * name is a placeholder. Other authors keep their saved name.
 */
export const resolveStaffNoteAuthorName = ({ note, authorUserId, authorDisplayName }: {
  note: Pick<MemberStaffNote, "createdByUserId" | "createdByDisplayName">;
  authorUserId: string;
  authorDisplayName: string;
}) => {
  const savedName = note.createdByDisplayName?.trim() || "";
  const currentName = authorDisplayName.trim();
  const savedNameUnresolved = unresolvedStaffNames.has(savedName.toLocaleLowerCase("en-US")) || savedName.includes("@");
  return savedNameUnresolved && currentName && authorUserId && note.createdByUserId === authorUserId
    ? currentName
    : savedName || "Staff name not recorded";
};

// CMP Dashboard (19) L15364-15372: a suspend, unsuspend or delete reason is
// logged as "Member suspended. Reason: X" with the type Status Change. CAP
// also logs "Member approved." (IF11 MB11) and a blank reason ends at the
// full stop.
// IF11 MB10: Cancel / Uncancel Member Renewal rows (CD:15806) are Status Changes too.
const statusChangeNotePattern = /^Member (suspended|unsuspended|deleted|approved|renewal cancelled\. Membership ends on the renewal date|renewal uncancelled\. Membership renewal restored)\.(?: Reason: [\s\S]*)?$/;

export const getMemberCommunicationLogType = (note: Pick<MemberStaffNote, "body" | "email">) =>
  note.email ? "Email" : statusChangeNotePattern.test(note.body) ? "Status Change" : "Notes/Comments";

const communicationEmailText = (email: MemberCommunicationEmail) => email.bodyFormat === "HTML" ? htmlToText(email.body) : email.body || "";

const isStaffNoteDateValid = (date: string) =>
  !date || DateTime.fromFormat(date, "yyyy-MM-dd", { locale: "en-US" }).isValid;

export const filterMemberStaffNotes = ({
  notes,
  date = "",
  authorUserId = "",
  keyword = "",
  chamberTimeZone,
  sort = null,
}: {
  notes: readonly MemberStaffNote[];
  date?: string;
  authorUserId?: string;
  keyword?: string;
  chamberTimeZone: string | null | undefined;
  sort?: StaffNoteSort | null;
}): MemberStaffNote[] => {
  // The editable calendar retains incomplete text. Do not pass that draft to
  // the strict time-zone formatter or treat it as an empty date filter.
  if (!isStaffNoteDateValid(date)) return [];

  const timeZone = normalizeCmpTimeZone(chamberTimeZone).timeZone;
  const firstInstant = date
    ? getTimeZoneCalendarDateStartUtcMilli(date, timeZone)
    : null;
  const lastInstant = date
    ? getTimeZoneCalendarDateEndUtcMilli(date, timeZone)
    : null;
  const search = keyword.trim().toLocaleLowerCase("en-US");
  const rows = notes.filter(
    (note) =>
      (firstInstant === null || note.createdAtUtcMilli >= firstInstant) &&
      (lastInstant === null || note.createdAtUtcMilli <= lastInstant) &&
      (!authorUserId || note.createdByUserId === authorUserId) &&
      (!search || `${note.body} ${note.email ? communicationEmailText(note.email) : ""}`.toLocaleLowerCase("en-US").includes(search)),
  );

  if (sort) {
    rows.sort((left, right) => {
      const comparison = sort.key === "date"
        ? left.createdAtUtcMilli - right.createdAtUtcMilli
        : sort.key === "staff" ? left.createdByDisplayName.localeCompare(right.createdByDisplayName)
          : getMemberCommunicationLogType(left).localeCompare(getMemberCommunicationLogType(right));
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }

  return rows;
};

export const getMemberStaffNoteAuthorOptions = (
  notes: readonly MemberStaffNote[],
) => {
  const authors = new Map<string, MemberStaffNote>();
  notes.forEach((note) => {
    const previous = authors.get(note.createdByUserId);
    if (!previous || note.createdAtUtcMilli > previous.createdAtUtcMilli) {
      authors.set(note.createdByUserId, note);
    }
  });
  return Array.from(authors.values())
    .map((note) => ({
      value: note.createdByUserId,
      label: note.createdByDisplayName,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
};

const inputClassName =
  "w-full rounded-lg border border-[#9E9E9E] bg-white px-[12px] py-[11px] text-sm leading-[normal] text-[#0A0A0A] outline-none focus:border-[#E1A731] disabled:cursor-not-allowed disabled:bg-[#F7F7F8]";
const tableRowClassName =
  "grid grid-cols-[minmax(110px,0.6fr)_minmax(150px,0.8fr)_minmax(0,2.4fr)]";

const CommunicationNote = ({ body }: { body: string }) => {
  const element = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const node = element.current;
    if (!node || expanded) return;
    const measure = () => setOverflows(node.scrollHeight > node.clientHeight + 1);
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(node);
    return () => observer?.disconnect();
  }, [body, expanded]);
  return <>
    <div ref={element} className={expanded ? "" : "line-clamp-2"}>{body}</div>
    {(overflows || expanded) && <button type="button" className="pt-[3px] text-[12px] font-bold text-[#E1A731]"
      aria-expanded={expanded} onClick={() => setExpanded(value => !value)}>{expanded ? "Read Less" : "Read More"}</button>}
  </>;
};

const MemberStaffNotesCard = ({
  id = "member-staff-notes",
  chamberId,
  notes,
  draftComment,
  onDraftCommentChange,
  onAdd,
  authorDisplayName,
  authorUserId,
  chamberTimeZone,
  pendingNote,
  loading = false,
  notesLoading = false,
  error,
  readOnly = false,
  highlightDraftChanges = false,
  communicationLog = false,
  emails = [],
}: MemberStaffNotesCardProps) => {
  const noun = useCmpOrganizationNoun();
  const [previewEmail, setPreviewEmail] = useState<MemberCommunicationEmail | null>(null);
  const rowClassName = communicationLog ? "grid grid-cols-[minmax(96px,0.6fr)_minmax(120px,0.8fr)_minmax(96px,0.6fr)_minmax(0,2.4fr)]" : tableRowClassName;
  const draftCommentRef = useRef(draftComment);
  draftCommentRef.current = draftComment;
  const [date, setDate] = useState("");
  const [staff, setStaff] = useState("");
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState<StaffNoteSort | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const saveInProgress = useRef(false);
  const savedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeZone = normalizeCmpTimeZone(chamberTimeZone).timeZone;
  const saveDisabled = loading || saving || readOnly;
  const displayedError = validationError || error;
  const dateError = isStaffNoteDateValid(date)
    ? null
    : `${DATE_FORMAT_MESSAGE} Or choose a date from the calendar.`;

  useEffect(() => () => {
    if (savedTimeout.current) {
      clearTimeout(savedTimeout.current);
    }
  }, []);

  const history = useMemo(() => {
    const rows: MemberStaffNote[] = [...notes.map(note => ({
      ...note, createdByDisplayName: resolveStaffNoteAuthorName({ note, authorUserId, authorDisplayName }),
    })), ...emails.map(email => ({
      id: `email-${email.id}`, body: email.subject, createdAtUtcMilli: email.sentAtUtcMilli,
      createdByUserId: email.senderUserId || `email-sender:${email.senderDisplayName || "unrecorded"}`,
      createdByDisplayName: email.senderDisplayName || "Sender not recorded", email,
    }))];
    if (pendingNote?.body.trim()) {
      rows.push({
        id: `${id}-pending-note`,
        body: pendingNote.body.trim(),
        createdAtUtcMilli: pendingNote.createdAtUtcMilli,
        createdByUserId: authorUserId,
        createdByDisplayName: authorDisplayName.trim() || "Staff name not recorded",
      });
    }
    return rows.sort((left, right) => right.createdAtUtcMilli - left.createdAtUtcMilli);
  }, [notes, emails, pendingNote, id, authorUserId, authorDisplayName]);
  const authorOptions = useMemo(() => getMemberStaffNoteAuthorOptions(history), [history]);
  const rows = useMemo(() => filterMemberStaffNotes({
    notes: history,
    date,
    authorUserId: staff,
    keyword,
    chamberTimeZone: timeZone,
    sort,
  }), [history, date, staff, keyword, timeZone, sort]);

  const saveComment = async () => {
    if (saveDisabled || saveInProgress.current) {
      return;
    }
    const body = draftComment.trim();
    if (!body || body.length > 4000) {
      setValidationError(body
        ? "Notes must be 4000 characters or fewer."
        : "Enter a note before saving.");
      return;
    }

    saveInProgress.current = true;
    setSaving(true);
    setValidationError(null);
    setSaved(false);
    try {
      if (await onAdd(body) === false) {
        return;
      }
      if (draftCommentRef.current.trim() === body) onDraftCommentChange("");
      setSaved(true);
      if (savedTimeout.current) {
        clearTimeout(savedTimeout.current);
      }
      savedTimeout.current = setTimeout(() => setSaved(false), 2500);
    } catch {
      setValidationError("The note could not be saved. Try again.");
    } finally {
      saveInProgress.current = false;
      setSaving(false);
    }
  };

  const renderSortHeader = (key: StaffNoteSort["key"], label: string) => (
    <div
      role="columnheader"
      aria-sort={sort?.key === key
        ? (sort.direction === "asc" ? "ascending" : "descending")
        : "none"}
    >
      <button
        type="button"
        title="Sort"
        aria-label={`Sort notes by ${key === "date" ? "date" : key === "staff" ? "staff member" : "type"}`}
        onClick={() => setSort((current) => ({
          key,
          direction: current?.key === key && current.direction === "asc" ? "desc" : "asc",
        }))}
        className="flex w-full select-none items-center gap-[6px] px-[14px] py-[12px] text-left text-[13px] font-bold text-[#0A0A0A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E1A731]"
      >
        {label}
        <svg width="9" height="14" viewBox="0 0 9 14" aria-hidden="true" className="shrink-0">
          <path d="M4.5 1 8 5H1z" fill={sort?.key === key && sort.direction === "asc" ? "#E1A731" : "#C4C7CC"} />
          <path d="M4.5 13 1 9h7z" fill={sort?.key === key && sort.direction === "desc" ? "#E1A731" : "#C4C7CC"} />
        </svg>
      </button>
    </div>
  );

  return (
    <>
    <SectionShell
      id={id}
      title={communicationLog ? "Member Communication Log" : "Staff Notes"}
      description={communicationLog ? <MemberEmailCaptureNotice chamberId={chamberId} /> : `Keep track of notes and valuable information about this member. Each note records the date it was made and the staff member who made it. Notes are visible to ${noun.singular} staff only.`}
      variant="design"
      contentGap={18}
    >
      <div className={communicationLog ? "" : "grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"}>
        {!communicationLog && <div className="flex min-w-0 flex-col gap-[6px]">
          <div id={`${id}-author-label`} className="text-sm font-bold text-[#0A0A0A]">Staff Member Making Note</div>
          <div aria-labelledby={`${id}-author-label`} className="break-words rounded-lg border border-[#ECECEC] bg-[#F7F7F8] px-[12px] py-[11px] text-sm text-[#0A0A0A]">{authorDisplayName.trim() || "Staff name not recorded"}</div>
          <span className="text-xs text-[#727272]">Auto-populated from your staff login.</span>
        </div>}
        <div className="flex min-w-0 flex-col gap-[6px]">
          <label htmlFor={`${id}-comment`} className="text-sm font-bold text-[#0A0A0A]">{communicationLog ? "Conversation Notes and Comments" : "Comment"} <span className="text-[#F0202E]">*</span></label>
          {communicationLog && <p className="text-sm">Enter your phone conversation, notes, or comments about this member.</p>}
          <textarea
            id={`${id}-comment`}
            rows={3}
            value={draftComment}
            onChange={(event) => {
              onDraftCommentChange(event.target.value);
              setValidationError(null);
            }}
            disabled={saveDisabled}
            aria-required="true"
            aria-invalid={!!displayedError}
            aria-describedby={displayedError ? `${id}-error` : undefined}
            placeholder={communicationLog ? "Enter the comment or note about this member" : "Enter the note or valuable information about this member"}
            className={`${inputClassName} min-h-[76px] resize-y ${displayedError ? "!border-[#E24C4C]" : ""}`}
            style={highlightDraftChanges && draftComment.trim() && !displayedError ? { outline: "2px solid #E1A731", outlineOffset: -1 } : undefined}
          />
          {displayedError && <p id={`${id}-error`} role="alert" className="text-[13px] text-[#F21B1B]">{displayedError}</p>}
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-5">
        <div className="flex flex-wrap items-center gap-[10px]">
          <p className={communicationLog ? "text-sm text-[#0A0A0A]" : "text-[13px] text-[#727272]"}>The date and staff member are recorded automatically when the note is added.</p>
          {saved && <span role="status" className="text-[13px] font-bold text-[#10B981]">Comments saved.</span>}
        </div>
        <button
          type="button"
          disabled={saveDisabled}
          aria-busy={loading || saving}
          onClick={() => { void saveComment(); }}
          className="rounded-lg bg-[#E1A731] px-[24px] py-[11px] text-sm font-semibold text-white hover:bg-[#C6912A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E1A731] disabled:cursor-not-allowed disabled:opacity-60"
        >{communicationLog ? "Save Comment" : "Save"}</button>
      </div>
      <h4 id={`${id}-history-heading`} className="mt-1.5 text-[15px] font-bold text-[#0A0A0A]">{communicationLog ? "Notes, Comments & Emails History" : "Notes History"}</h4>
      <div className="grid items-end gap-[14px] sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.6fr)_auto]">
        <div className="flex min-w-0 flex-col gap-[6px]">
          <label htmlFor={`${id}-date`} className="text-[13px] font-bold text-[#0A0A0A]">Filter by Date</label>
          <CalendarDateInput
            appearance="claude" yearRange="historical"
            id={`${id}-date`}
            ariaLabel="Filter notes by date"
            ariaInvalid={dateError ? true : undefined}
            ariaDescribedBy={dateError ? `${id}-date-error` : undefined}
            mode="fullDate"
            value={date}
            onChange={setDate}
            placeholder="Select Date"
            inputClassName={`h-[42px] w-full rounded-lg border border-[#ECECEC] bg-white px-3 text-sm text-[#0A0A0A] outline-none focus:border-[#E1A731] ${dateError ? "!border-[#E24C4C]" : ""}`}
          />
          {dateError && <p id={`${id}-date-error`} role="alert" className="text-[13px] text-[#F21B1B]">{dateError}</p>}
        </div>
        <div className="flex min-w-0 flex-col gap-[6px]">
          <label htmlFor={`${id}-staff`} className="text-[13px] font-bold text-[#0A0A0A]">Filter by Staff Member</label>
          <SearchableSelectField id={`${id}-staff`} options={authorOptions} value={staff} onChange={setStaff} placeholder="All Staff" searchPlaceholder="Search Staff" />
        </div>
        <div className="flex min-w-0 flex-col gap-[6px]">
          <label htmlFor={`${id}-keywords`} className="text-[13px] font-bold text-[#0A0A0A]">Search Keywords</label>
          <input id={`${id}-keywords`} type="text" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder={communicationLog ? "Search comments, notes, and email subjects" : "Search comments by keyword"} className={`${inputClassName} h-[42px]`} />
        </div>
        <button type="button" onClick={() => { setDate(""); setStaff(""); setKeyword(""); }} className="h-[42px] rounded-lg bg-[#F21B1B] px-[26px] text-sm font-bold text-white hover:bg-[#D01414] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F21B1B]">{communicationLog ? "Clear All Filter" : "Clear Filters"}</button>
      </div>
      <div className="overflow-x-auto rounded-[10px] border border-[#ECECEC]">
        <div role="table" aria-labelledby={`${id}-history-heading`} aria-busy={notesLoading} className={communicationLog ? "min-w-[740px]" : "min-w-[620px]"}>
          <div role="row" className={`${rowClassName} bg-[#F7F7F8]`}>
            {renderSortHeader("date", "Date")}
            {renderSortHeader("staff", "Made By")}
            {communicationLog && renderSortHeader("type", "Type")}
            <div role="columnheader" className="px-[14px] py-[12px] text-[13px] font-bold text-[#0A0A0A]">{communicationLog ? "Details" : "Comment"}</div>
          </div>
          {rows.map((note) => (
            <div key={note.id} role="row" tabIndex={note.email ? 0 : undefined}
              aria-label={note.email ? `Email: ${note.body}. Open email preview` : undefined}
              onClick={note.email ? event => {
                if (event.target instanceof Element && event.target.closest("button, a, input, textarea, select, [role='button']")) return;
                setPreviewEmail(note.email || null);
              } : undefined}
              onKeyDown={note.email ? event => {
                if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
                event.preventDefault();
                setPreviewEmail(note.email || null);
              } : undefined}
              className={`${rowClassName} border-t border-[#ECECEC] text-[13px] text-[#0A0A0A] ${note.email ? "cursor-pointer hover:bg-[#FAFAFB] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-cpaGold" : ""}`}>

              <div role="cell" className="whitespace-nowrap px-[14px] py-[13px]">{DateTime.fromMillis(note.createdAtUtcMilli, { zone: timeZone }).toFormat("MMM d, yyyy", { locale: "en-US" })}{communicationLog && <div className="mt-[2px] text-[12px] text-[#727272]">{DateTime.fromMillis(note.createdAtUtcMilli, { zone: timeZone }).toFormat("h:mm a", { locale: "en-US" })}</div>}</div>
              <div role="cell" className="break-words px-[14px] py-[13px] font-semibold">{note.createdByDisplayName}</div>
              {communicationLog && <div role="cell" className="px-[14px] py-[13px]">{getMemberCommunicationLogType(note)}</div>}
              <div role="cell" className="whitespace-pre-wrap break-words px-[14px] py-[13px] leading-[1.5]">
                {note.email ? <>
                  <button type="button" className="w-full text-left font-bold" onClick={() => setPreviewEmail(note.email || null)}>{note.body}</button>
                  {note.email.body && <div className="mt-[3px] text-[#727272]"><CommunicationNote body={communicationEmailText(note.email)} /></div>}
                </> : communicationLog ? <CommunicationNote body={note.body} /> : note.body}
              </div>
            </div>
          ))}
          {!rows.length && <div role="row" className="border-t border-[#ECECEC]"><div role="cell" aria-colspan={communicationLog ? 4 : 3} className="px-[14px] py-[18px] text-[13px] text-[#727272]">{dateError ? "Enter a valid date to filter this history." : notesLoading ? "Loading notes..." : communicationLog ? "No comments, notes, or emails match the current filters." : "No notes match the current filters."}</div></div>}
        </div>
      </div>
    </SectionShell>
    <MemberCommunicationEmailPreview email={previewEmail} onClose={() => setPreviewEmail(null)} timeZone={timeZone} />
    </>
  );
};

export default MemberStaffNotesCard;
